import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { DeckViewer } from '@/components/DeckViewer'
import type { SlideInfo } from '@/components/DeckViewer'
import { CommentDrawer } from '@/components/CommentDrawer'
import { SpeakerNotesOverlay } from '@/components/SpeakerNotes'
import { openSpeakerNotesWindow } from '@/components/speakerNotesWindow'
import { deckLoaders, getDeckMeta, docSlug } from '@/decks/registry'
import type { DeckDoc, DeckTheme } from '@/decks/registry'
import { DeckHelmet } from '@/components/DeckHelmet'
import { useThemeColor } from '@/hooks/useThemeColor'
import { createSlideSync } from '@/lib/slide-sync'

function themeStorageKey(slug: string | undefined): string {
  return `gc-slides-theme:${slug ?? ''}`
}

function langStorageKey(slug: string | undefined): string {
  return `gc-slides-lang:${slug ?? ''}`
}

function resolveInitialLang(slug: string | undefined): string {
  return (slug ? localStorage.getItem(langStorageKey(slug)) : null) ?? 'en'
}

function resolveInitialTheme(slug: string | undefined): DeckTheme {
  const stored = slug ? localStorage.getItem(themeStorageKey(slug)) : null
  if (stored === 'dark' || stored === 'light') return stored
  return getDeckMeta(slug ?? '')?.defaultTheme ?? 'light'
}

// 1-based slide param → 0-based index (NaN/invalid → 0).
function slideParamToIndex(slide: string | undefined): number {
  const n = parseInt(slide ?? '', 10)
  return Number.isInteger(n) && n >= 1 ? n - 1 : 0
}

export function DeckPage() {
  const { slug, slide } = useParams<{ slug: string; slide: string }>()
  const navigate = useNavigate()
  const routeIndex = slideParamToIndex(slide)

  const [slideIndex, setSlideIndex] = useState(routeIndex)
  const [slideCount, setSlideCount] = useState(1)
  const [slides, setSlides] = useState<SlideInfo[]>([])
  const [docs, setDocs] = useState<DeckDoc[]>([])
  const [notesOverlayOpen, setNotesOverlayOpen] = useState(false)
  const [theme, setTheme] = useState<DeckTheme>(() => resolveInitialTheme(slug))
  const [lang, setLang] = useState<string>(() => resolveInitialLang(slug))

  const engineRef = useRef<{ goTo: (n: number) => void } | null>(null)
  const syncRef = useRef<ReturnType<typeof createSlideSync> | null>(null)
  // react-router's navigate changes identity on each navigation; keep it in a
  // ref so our handlers stay stable (otherwise DeckViewer reloads every nav).
  const navigateRef = useRef(navigate)
  useEffect(() => { navigateRef.current = navigate })
  // Refs so callbacks read current values without being deps (avoids re-creating
  // handlers / sync loops). The URL is the single source of truth for the slide.
  const routeIndexRef = useRef(routeIndex)
  const slideIndexRef = useRef(routeIndex) // engine's current slide
  const slideCountRef = useRef(slideCount)
  const slugRef = useRef(slug)
  useEffect(() => { routeIndexRef.current = routeIndex }, [routeIndex])
  useEffect(() => { slideCountRef.current = slideCount }, [slideCount])
  useEffect(() => { slugRef.current = slug }, [slug])

  const handleEngineReady = useCallback((engine: { goTo: (n: number) => void }) => {
    engineRef.current = engine
  }, [])

  const handleDeckLoaded = useCallback((count: number, slideInfos: SlideInfo[]) => {
    setSlideCount(count)
    setSlides(slideInfos)
  }, [])

  const handleDocsLoaded = useCallback((deckDocs: DeckDoc[]) => {
    setDocs(deckDocs)
  }, [])

  // React buttons navigate the URL; the effect below drives the engine.
  const goToSlide = useCallback((n: number) => {
    if (!slugRef.current) return
    const clamped = Math.max(0, Math.min(n, slideCountRef.current - 1))
    navigateRef.current(`/deck/${slugRef.current}/${clamped + 1}`, { replace: true })
  }, [])
  const goPrev = useCallback(() => { goToSlide(routeIndexRef.current - 1) }, [goToSlide])
  const goNext = useCallback(() => { goToSlide(routeIndexRef.current + 1) }, [goToSlide])

  // Engine → URL: the deck's own controls (arrow keys, deck clicks) move the
  // engine; reflect that in the URL so it stays the source of truth.
  const handleSlideChange = useCallback((index: number) => {
    setSlideIndex(index)
    slideIndexRef.current = index
    syncRef.current?.broadcast(index)
    if (slugRef.current && index !== routeIndexRef.current) {
      navigateRef.current(`/deck/${slugRef.current}/${index + 1}`, { replace: true })
    }
  }, [])

  // URL → engine: keep the engine on the slide named by the route (deep link,
  // back/forward, button nav). goTo is idempotent so this never loops.
  useEffect(() => {
    engineRef.current?.goTo(routeIndex)
  }, [routeIndex])

  // Docs live on their own dedicated page (/deck/:slug/doc/:docId).
  // Open in a new tab so the deck stays put behind it.
  const openDocs = useCallback(() => {
    if (!slug || docs.length === 0) return
    window.open(`/deck/${slug}/doc/${docSlug(docs[0].title)}`, '_blank', 'noopener,noreferrer')
  }, [slug, docs])

  // BroadcastChannel sync — listen for external slide changes.
  useEffect(() => {
    const sync = createSlideSync((index) => { engineRef.current?.goTo(index) })
    syncRef.current = sync
    return () => sync.close()
  }, [])

  // Speaker notes
  const hasNotes = useMemo(() => slides.some(s => s.notes), [slides])
  const openNotes = useCallback(() => { setNotesOverlayOpen(true) }, [])
  const closeNotes = useCallback(() => { setNotesOverlayOpen(false) }, [])
  const handleOpenNotesWindow = useCallback(() => {
    openSpeakerNotesWindow(slides, slideIndexRef.current)
    setNotesOverlayOpen(false)
  }, [slides])

  // Theme: re-resolve on deck change, apply to <body>, persist on toggle.
  useEffect(() => { setTheme(resolveInitialTheme(slug)) }, [slug])
  useEffect(() => {
    document.body.setAttribute('data-theme', theme)
    return () => { document.body.removeAttribute('data-theme') }
  }, [theme])
  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const next: DeckTheme = prev === 'dark' ? 'light' : 'dark'
      if (slug) localStorage.setItem(themeStorageKey(slug), next)
      return next
    })
  }, [slug])

  // Language: the switcher offers the languages the current slide provides.
  const slideLangs = useMemo(
    () => slides[slideIndex]?.langs ?? ['en'],
    [slides, slideIndex],
  )
  useEffect(() => { setLang(resolveInitialLang(slug)) }, [slug])
  // A slide without the active translation falls back to English on screen.
  const activeLang = slideLangs.includes(lang) ? lang : 'en'
  useEffect(() => {
    document.body.setAttribute('data-lang', activeLang)
    return () => { document.body.removeAttribute('data-lang') }
  }, [activeLang])
  const selectLang = useCallback((next: string) => {
    setLang(next)
    if (slug) localStorage.setItem(langStorageKey(slug), next)
  }, [slug])

  const darkActive = theme === 'dark' || (slides[slideIndex]?.isDark ?? false)
  useThemeColor(darkActive ? '#111111' : '#FAFAFA')

  // Unknown deck → send the user back to the home listing.
  if (!slug || !deckLoaders[slug]) {
    return <Navigate to="/" replace />
  }

  return (
    <>
      <DeckHelmet slug={slug} />
      <DeckViewer
        slug={slug}
        initialSlide={slideParamToIndex(slide)}
        onSlideChange={handleSlideChange}
        onDeckLoaded={handleDeckLoaded}
        onEngineReady={handleEngineReady}
        onDocsLoaded={handleDocsLoaded}
      />
      <CommentDrawer
        deckSlug={slug}
        slideIndex={slideIndex}
        slideCount={slideCount}
        slides={slides}
        onPrev={goPrev}
        onNext={goNext}
        onJumpTo={goToSlide}
        hasDocs={docs.length > 0}
        onOpenDocs={openDocs}
        hasNotes={hasNotes}
        onOpenNotes={openNotes}
        theme={theme}
        onToggleTheme={toggleTheme}
        lang={activeLang}
        langs={slideLangs}
        onSelectLang={selectLang}
      />
      {notesOverlayOpen && (
        <SpeakerNotesOverlay
          slides={slides}
          currentSlide={slideIndex}
          onPrev={goPrev}
          onNext={goNext}
          onClose={closeNotes}
          onOpenWindow={handleOpenNotesWindow}
        />
      )}
    </>
  )
}
